import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    LinearProgress,
    Button,
    Tab,
    Tabs,
    IconButton,
    Tooltip,
    Fade,
    Zoom,
    Avatar,
    Stack,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    Lock as LockedIcon,
    CheckCircle as UnlockedIcon,
    Stars as ProgressIcon,
    EmojiEvents as TrophyIcon,
    Speed as SpeedIcon,
    LocalFireDepartment as StreakIcon,
    School as QuizIcon,
    WorkspacePremium as PerfectIcon,
    Timeline as XpIcon,
    AutoAwesome as SpecialIcon,
} from '@mui/icons-material';
import { createGamificationService } from '../services/GamificationService';

const AchievementCard = ({ achievement, isUnlocked, progress, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const theme = useTheme();

    const getAchievementIcon = (type) => {
        const icons = {
            first_quiz: <QuizIcon />,
            quiz_completion: <TrophyIcon />,
            perfect_score: <PerfectIcon />,
            perfectionist: <PerfectIcon />,
            streak: <StreakIcon />,
            streak_3: <StreakIcon />,
            streak_7: <StreakIcon />,
            xp_milestone: <XpIcon />,
            speed_demon: <SpeedIcon />,
            special: <SpecialIcon />,
        };
        return icons[type] || <TrophyIcon />;
    };

    const getRarityGradient = (rarity) => {
        const colors = theme.palette.gamification;
        const gradients = {
            bronze: `linear-gradient(135deg, ${colors.bronze}, ${theme.palette.mode === 'dark' ? '#8B4513' : '#654321'})`,
            silver: `linear-gradient(135deg, ${colors.silver}, ${theme.palette.mode === 'dark' ? '#808080' : '#6b7280'})`,
            gold: `linear-gradient(135deg, ${colors.gold}, ${theme.palette.mode === 'dark' ? '#FFA500' : '#d97706'})`,
            legendary: `linear-gradient(135deg, ${colors.legendary}, ${theme.palette.mode === 'dark' ? '#E91E63' : '#be185d'})`
        };
        return gradients[rarity] || gradients.bronze;
    };

    const getRarityColor = (rarity) => {
        const colorMap = {
            bronze: 'warning',
            silver: 'info',
            gold: 'success',
            legendary: 'error'
        };
        return colorMap[rarity] || 'default';
    };

    const progressPercentage = isUnlocked ? 100 : (progress / achievement.required_value) * 100;

    return (
        <Zoom in timeout={300}>
            <Card
                onClick={() => onClick && onClick(achievement)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    height: '100%',
                    cursor: onClick ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    transform: hovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0)',
                    boxShadow: hovered 
                        ? `0 8px 25px rgba(0,0,0,0.15), 0 0 20px ${isUnlocked ? theme.palette.gamification.success : theme.palette.primary.main}30`
                        : isUnlocked 
                            ? `0 4px 20px ${theme.palette.gamification.success}20`
                            : 2,
                    opacity: isUnlocked ? 1 : 0.7,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: getRarityGradient(achievement.rarity),
                    }
                }}
            >
                <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Avatar
                            sx={{
                                width: 48,
                                height: 48,
                                mr: 2,
                                background: isUnlocked ? getRarityGradient(achievement.rarity) : 'grey.400',
                                color: 'white',
                                filter: isUnlocked ? 'none' : 'grayscale(100%)',
                            }}
                        >
                            {isUnlocked ? (
                                <span style={{ fontSize: '24px' }}>{achievement.icon}</span>
                            ) : (
                                <LockedIcon />
                            )}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                lineHeight: 1.2,
                                mb: 0.5
                            }}>
                                {achievement.name}
                            </Typography>
                            <Chip
                                label={achievement.rarity.toUpperCase()}
                                size="small"
                                color={getRarityColor(achievement.rarity)}
                                variant={isUnlocked ? 'filled' : 'outlined'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                        </Box>
                        {isUnlocked && (
                            <UnlockedIcon sx={{ color: 'success.main', ml: 1 }} />
                        )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {achievement.description}
                    </Typography>

                    {!isUnlocked && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Progress
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {progress}/{achievement.required_value}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        background: getRarityGradient(achievement.rarity),
                                    },
                                }}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                label={`${achievement.xp_reward} XP`}
                                size="small"
                                sx={{ 
                                    fontSize: '0.7rem',
                                    bgcolor: theme.palette.gamification.xp,
                                    color: 'white',
                                    '&:hover': { bgcolor: theme.palette.primary.dark }
                                }}
                            />
                            <Chip
                                label={`${achievement.coin_reward} Coins`}
                                size="small"
                                sx={{ 
                                    fontSize: '0.7rem',
                                    bgcolor: theme.palette.gamification.coins,
                                    color: theme.palette.mode === 'dark' ? 'black' : 'white',
                                    '&:hover': { bgcolor: theme.palette.gamification.warning }
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Zoom>
    );
};

const AchievementsModal = ({ open, onClose, userId, accessToken }) => {
    const [achievements, setAchievements] = useState([]);
    const [userStats, setUserStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const theme = useTheme();

    useEffect(() => {
        const loadData = async () => {
            if (!open) return;

            try {
                setLoading(true);
                const gamificationService = createGamificationService(accessToken);
                
                const [achievementsData, statsData] = await Promise.all([
                    gamificationService.getAchievements(userId),
                    gamificationService.getUserStats()
                ]);
                
                setAchievements(achievementsData);
                setUserStats(statsData);
                setError(null);
            } catch (err) {
                console.error('Failed to load data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [open, userId, accessToken]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const getFilteredAchievements = () => {
        switch (selectedTab) {
            case 0: // All
                return achievements;
            case 1: // Unlocked
                return achievements.filter(a => a.is_unlocked);
            case 2: // Locked
                return achievements.filter(a => !a.is_unlocked);
            default:
                return achievements;
        }
    };

    const getStatistics = () => {
        const total = achievements.length;
        const unlocked = achievements.filter(a => a.is_unlocked).length;
        // Use user's actual stats instead of calculating from achievements
        const totalXp = userStats?.total_xp || 0;
        const totalCoins = userStats?.total_coins || 0;

        return { total, unlocked, totalXp, totalCoins };
    };

    const stats = getStatistics();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: theme.palette.background.gamification,
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrophyIcon sx={{ mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Achievements
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Statistics Bar */}
                <Box sx={{ 
                    p: 3, 
                    background: theme.palette.background.gamification,
                    color: 'white'
                }}>
                    <Grid container spacing={3}>
                        <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    {stats.unlocked}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Unlocked
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    {stats.total}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Total
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    {stats.totalXp.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Current XP
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    {stats.totalCoins.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Current Coins
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                    <Tabs value={selectedTab} onChange={handleTabChange}>
                        <Tab label={`All (${achievements.length})`} />
                        <Tab label={`Unlocked (${stats.unlocked})`} />
                        <Tab label={`Locked (${stats.total - stats.unlocked})`} />
                    </Tabs>
                </Box>

                {/* Achievements Grid */}
                <Box sx={{ p: 3 }}>
                    {loading ? (
                        <Typography>Loading achievements...</Typography>
                    ) : error ? (
                        <Typography color="error">Error: {error}</Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {getFilteredAchievements().map((achievementData, index) => (
                                <Grid item xs={12} sm={6} md={4} key={achievementData.achievement.id}>
                                    <Fade in timeout={200 + index * 50}>
                                        <div>
                                            <AchievementCard
                                                achievement={achievementData.achievement}
                                                isUnlocked={achievementData.is_unlocked}
                                                progress={achievementData.progress}
                                            />
                                        </div>
                                    </Fade>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export { AchievementsModal };
